import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Grid,
  InputAdornment,
  Typography
} from '@material-ui/core';
import i18n from 'i18next';
import IconButton from '@material-ui/core/Icon';
import { LoginContainer } from 'src/components/loginContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { InputField } from '../../components/input-field';
import { useMounted } from '../../hooks/use-mounted';
import { selectAuth, signin, resetStatus } from './authSlice';
import { Mail as MailIcon } from '../../icons/mail';
import { LockClosed } from '../../icons/lock-closed';
import VisibilityIcon from '../../icons/visibilityIcon';
import VisibilityIconOff from '../../icons/visibilityIconOff';

const LoginPage: FC = () => {
  const mounted = useMounted();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = React.useState(false);
  const { error } = useAppSelector(selectAuth);
  const emailRegex = RegExp(
    /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@+(eurafric-information.com)|(bankofafrica.ma)/
  );

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .matches(emailRegex, 'Email non valide')
        .max(255)
        .required('Champ obligatoire'),
      password: Yup.string().max(255).required('Mot de passe obligatoire'),
      policy: Yup.boolean()
    }),
    onSubmit: async (values, helpers) => {
      try {
        await dispatch(signin({ email: values.email, password: values.password }));
        if (mounted.current) {
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted.current) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    }
  });
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handlClickForgotPass = () => {
    dispatch(resetStatus());
    navigate('/password-recovery');
  };
  return (
    <LoginContainer>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            mb: 3
          }}
        >
          <Typography color="textPrimary" variant="h3">
            Authentification
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputField
              error={Boolean(formik.touched.email && formik.errors.email)}
              fullWidth
              helperText={formik.touched.email && formik.errors.email}
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="email"
              value={formik.values.email}
              placeholder="demo@bmcebank.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <InputField
              error={Boolean(formik.touched.password && formik.errors.password)}
              fullWidth
              helperText={formik.touched.password && formik.errors.password}
              name="password"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              placeholder="Votre mot de passe"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockClosed />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      data-testid="showPassword"
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      style={{ marginRight: '0.5vh', marginTop: '0.5vh', cursor: 'pointer' }}
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityIconOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          {formik.errors.submit && (
            <Grid item xs={12}>
              <FormHelperText error>{formik.errors.submit as React.ReactNode}</FormHelperText>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              color="primary"
              disabled={formik.isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              Se connecter
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button color="primary" onClick={handlClickForgotPass} variant="text">
              Mot de passe oublié
            </Button>
          </Grid>
        </Grid>
      </form>
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {i18n.t(`auth.errors.${error}`)}
        </Alert>
      )}
    </LoginContainer>
  );
};
export default LoginPage;
